import type { AiChatResponse, AiConfirmationDraft, AiSessionMessage, AiTableResult, ChatFileResult } from 'src/app/api-client';

export interface AiAttachmentRef {
  id: string;
  fileName: string;
}

export type AiResponseType = 'text' | 'table' | 'pending_confirmation' | 'file' | 'error';

export type AiRowOutcome = 'saved' | 'removed';

/** One row in a chat thread — strict view model over the all-optional generated DTOs. */
export interface AiThreadMessage {
  localId: number;
  /** 'notice' = a system line (refusal, failure, draft outcome); never sent to the model. */
  role: 'user' | 'assistant' | 'notice';
  text: string;
  responseType: AiResponseType | null;
  table: AiTableResult | null;
  draft: AiConfirmationDraft | null;
  /** A generated file (board-report workbook) offered for download. */
  file: ChatFileResult | null;
  /** Only a draft from THIS session's live reply can be confirmed; reloaded drafts are history. */
  draftActionable: boolean;
  /** A user message whose turn failed or was refused — the thread offers Retry. */
  failed: boolean;
  /** P171 batch card: rows already saved or removed (by index), so a re-rendered card never offers them again. */
  rowOutcomes?: Readonly<Record<number, AiRowOutcome>>;
  attachments: AiAttachmentRef[];
}

let nextLocalId = 1;
export const newLocalId = (): number => nextLocalId++;

export function userMessage(text: string, attachments: AiAttachmentRef[]): AiThreadMessage {
  return { localId: newLocalId(), role: 'user', text, responseType: null, table: null, draft: null, file: null, draftActionable: false, failed: false, attachments };
}

export function noticeMessage(text: string): AiThreadMessage {
  return { localId: newLocalId(), role: 'notice', text, responseType: null, table: null, draft: null, file: null, draftActionable: false, failed: false, attachments: [] };
}

export function fromResponse(r: AiChatResponse): AiThreadMessage {
  const type = (r.Type ?? 'text') as AiResponseType;
  return {
    localId: newLocalId(), role: 'assistant', text: r.Text ?? '', responseType: type,
    table: r.Table ?? null, draft: r.Draft ?? null, file: r.File ?? null, draftActionable: type === 'pending_confirmation',
    failed: false, attachments: [],
  };
}

/**
 * Stored messages: for table/draft replies the server keeps a replay rendition in Content
 * (markdown table / "not saved" note) and the original response in Payload — render the Payload.
 */
export function fromServerMessage(m: AiSessionMessage): AiThreadMessage {
  const payload = m.Payload ?? null;
  const isAssistant = m.Role === 'assistant';
  return {
    localId: newLocalId(),
    role: isAssistant ? 'assistant' : 'user',
    text: payload ? payload.Text ?? '' : m.Content ?? '',
    responseType: isAssistant ? ((m.ResponseType ?? 'text') as AiResponseType) : null,
    table: payload?.Table ?? null,
    draft: payload?.Draft ?? null,
    file: payload?.File ?? null,
    draftActionable: false,
    failed: !!m.Failed,
    attachments: (m.Attachments ?? []).map((a) => ({ id: a.AttachmentId ?? '', fileName: a.FileName ?? '' })),
  };
}
